class BundleProductForm extends HTMLElement {
  constructor() {
    super();
    
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type="submit"]');
    this.buyNowButton = this.querySelector('.shopify-payment-button__button');
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    
    if (this.form) {
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    }
    
    // Intercept Shopify's buy now button
    if (this.buyNowButton) {
      this.buyNowButton.addEventListener('click', this.onBuyNowHandler.bind(this));
    }
    
    if (document.querySelector('cart-drawer')) {
      this.submitButton.setAttribute('aria-haspopup', 'dialog');
    }
  }
  
  onSubmitHandler(evt) {
    evt.preventDefault();
    
    if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
    
    this.handleErrorMessage();
    this.submitButton.setAttribute('aria-disabled', true);
    this.submitButton.classList.add('loading');
    
    const bundleProducts = JSON.parse(this.dataset.bundleProducts || '[]');
    
    if (this.cart) {
      this.cart.setActiveElement(document.activeElement);
    }
    
    if (bundleProducts.length > 0) {
      // Add bundle products to cart
      this.addBundleToCart(bundleProducts, false);
    } else {
      // Fallback to regular add to cart
      this.addRegularToCart();
    }
  }
  
  onBuyNowHandler(evt) {
    evt.preventDefault();
    evt.stopPropagation();
    
    const bundleProducts = JSON.parse(this.dataset.bundleProducts || '[]');
    
    if (bundleProducts.length > 0) {
      // Add bundle products and redirect to checkout
      this.addBundleToCart(bundleProducts, true);
    } else {
      // Allow default buy now behavior
      return true;
    }
  }
  
  async addBundleToCart(bundleProducts, buyNow = false) {
    const items = bundleProducts.map(product => ({
      id: product.variant_id,
      quantity: 1
    }));
    
    // Add items to cart using the JSON endpoint
    const addConfig = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items })
    };
    
    try {
      const addResponse = await fetch('/cart/add.js', addConfig);
      const addData = await addResponse.json();
      
      if (!addResponse.ok || addData.status === 'error') {
        throw new Error(addData.description || addData.message || 'Failed to add bundle to cart');
      }
      
      if (buyNow) {
        // Redirect to checkout
        window.location.href = '/checkout';
        return;
      }
      
      // Fetch updated sections for cart UI
      if (this.cart) {
        const sectionsToRender = this.cart.getSectionsToRender();
        const sectionIds = sectionsToRender.map((section) => section.id).join(',');
        
        // Fetch sections with cart state
        const sectionsResponse = await fetch(`${window.location.pathname}?sections=${sectionIds}`);
        const sectionsData = await sectionsResponse.json();
        
        // Update each section manually
        sectionsToRender.forEach((section) => {
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          
          if (!sectionElement || !sectionsData[section.id]) return;
          
          // Parse the HTML and extract the content we need
          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionsData[section.id], 'text/html');
          
          // For sections with a specific selector, find that element in the parsed HTML
          // Otherwise, look for .shopify-section
          let newContent;
          if (section.selector) {
            newContent = doc.querySelector(section.selector);
          } else {
            newContent = doc.querySelector('.shopify-section');
          }
          
          if (newContent) {
            sectionElement.innerHTML = newContent.innerHTML;
          }
        });
        
        // Remove is-empty class if cart has items
        if (this.cart.classList.contains('is-empty')) {
          this.cart.classList.remove('is-empty');
        }
        
        // Now open the cart drawer with a small delay to let DOM update
        setTimeout(() => {
          if (this.cart.open) {
            this.cart.open();
          }
        }, 200);
      } else {
        // Fallback: redirect to cart
        window.location.href = window.routes.cart_url || '/cart';
      }
      
    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      this.handleErrorMessage(error.message);
    } finally {
      this.submitButton.classList.remove('loading');
      this.submitButton.removeAttribute('aria-disabled');
    }
  }
  
  async addRegularToCart() {
    const formData = new FormData(this.form);
    
    // Add sections to fetch for cart update
    if (this.cart) {
      formData.append(
        'sections',
        this.cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
    }
    
    const config = {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    };
    
    try {
      const response = await fetch(window.routes.cart_add_url || '/cart/add', config);
      const data = await response.json();
      
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Failed to add to cart');
      }
      
      // Update cart UI
      if (this.cart) {
        this.cart.renderContents(data);
      } else {
        // Fallback: redirect to cart
        window.location.href = window.routes.cart_url || '/cart';
      }
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.handleErrorMessage(error.message);
    } finally {
      this.submitButton.classList.remove('loading');
      this.submitButton.removeAttribute('aria-disabled');
    }
  }
  
  handleErrorMessage(errorMessage = false) {
    const errorMessageWrapper = this.querySelector('.product-form__error-message-wrapper');
    if (!errorMessageWrapper) return;
    
    const errorMessageElement = errorMessageWrapper.querySelector('.product-form__error-message');
    
    if (errorMessage) {
      errorMessageElement.textContent = errorMessage;
      errorMessageWrapper.removeAttribute('hidden');
    } else {
      errorMessageWrapper.setAttribute('hidden', '');
      errorMessageElement.textContent = '';
    }
  }
}

customElements.define('bundle-product-form', BundleProductForm);
